import type {ConnectionADto} from '@cohort/admin-schemas/connection';
import {ConnectionNameSchema} from '@cohort/admin-schemas/connection';
import Button from '@cohort/merchants/components/buttons/Button';
import Editabletext from '@cohort/merchants/components/form/v2/Editabletext';
import DeletionModal from '@cohort/merchants/components/modals/DeletetionModal';
import Table from '@cohort/merchants/components/Table';
import {connectionsKeys} from '@cohort/merchants/hooks/api/Connections';
import {useCohortMutation} from '@cohort/merchants/hooks/api/Query';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {deleteConnection, patchConnection} from '@cohort/merchants/lib/api/Connections';
import EditConnectionModal from '@cohort/merchants/pages/connections/new/EditConnectionModal';
import {notifyError} from '@cohort/merchants/stores/ErrorModalStore';
import type {AppSpec} from '@cohort/shared/schema/apps/app';
import {formatDate} from '@cohort/shared/utils/format';
import {CohortError} from '@cohort/shared-frontend/common/errors';
import {Trash} from '@phosphor-icons/react';
import {useQueryClient} from '@tanstack/react-query';
import {Fragment, useState} from 'react';
import {useTranslation} from 'react-i18next';

type ConnectionsTableProps = {
  appSpec: AppSpec;
  merchantConnections: ConnectionADto[];
  isConnectionsLoading: boolean;
};

const ConnectionsTable: React.FC<ConnectionsTableProps> = ({
  appSpec,
  merchantConnections,
  isConnectionsLoading,
}) => {
  const [connectionToConfigure, setConnectionToConfigure] = useState<ConnectionADto | null>(null);
  const [connectionToDelete, setConnectionToDelete] = useState<ConnectionADto | null>(null);
  const {t} = useTranslation('pages', {keyPrefix: 'settings.apps.connectionsTable'});
  const merchant = useCurrentMerchant();
  const queryClient = useQueryClient();

  const {mutate: updateConnectionName} = useCohortMutation({
    mutationFn: async (args: {connectionId: string; name: string}) =>
      patchConnection(merchant.id, args.connectionId, {name: args.name}),
    notifySuccessMessage: t('patchNotificationSuccess'),
    onSuccess: async () => {
      await queryClient.invalidateQueries(connectionsKeys.list(merchant.id));
    },
  });

  const {mutate: deleteMerchantConnection} = useCohortMutation({
    mutationFn: async (connectionId: string) => deleteConnection(merchant.id, connectionId),
    notifySuccessMessage: t('deleteNotificationSuccess'),
    onSuccess: async () => {
      await queryClient.invalidateQueries(connectionsKeys.list(merchant.id));
    },
    onError: error => {
      if (error instanceof CohortError && error.code === 'connection.in-use') {
        notifyError(error, t('deleteInUseErrorNotification'));
      } else {
        notifyError(error, t('deleteErrorNotification'));
      }
    },
  });

  return (
    <Fragment>
      <Table
        headers={[]}
        isLoading={isConnectionsLoading}
        emptyState={{
          isEmpty: merchantConnections.length === 0,
          title: t('titleEmpty'),
          description: t('descriptionEmpty'),
        }}
      >
        {merchantConnections.map(connection => (
          <tr key={connection.id}>
            <td>
              <div
                data-testid="connection-name"
                className="w-fit whitespace-nowrap py-4 pr-3 text-sm font-medium"
              >
                <Editabletext
                  value={connection.name}
                  textSize="small"
                  key={connection.status} // @Devs, Auto update the component when the connection status changes.
                  onChange={(name: string) =>
                    updateConnectionName({
                      connectionId: connection.id,
                      name,
                    })
                  }
                  validator={(connectionName: string) =>
                    ConnectionNameSchema.safeParse(connectionName).success
                  }
                />
              </div>
            </td>
            <td className="w-1/4 whitespace-nowrap py-4 pr-3 text-sm">
              {connection.status === 'ready' && formatDate(connection.createdAt)}
              {connection.status === 'config_needed' && (
                <Button onClick={() => setConnectionToConfigure(connection)}>
                  {t('buttonConfigure')}
                </Button>
              )}
            </td>
            <td width="100px" className="py-4 pr-3">
              <Button variant="secondary" onClick={() => setConnectionToDelete(connection)}>
                <Trash size={20} className="text-red-500" />
              </Button>
            </td>
          </tr>
        ))}
      </Table>
      {connectionToDelete !== null && (
        <DeletionModal
          show
          title={t('modalTitle')}
          subtitle={t('modalText', {connectionName: connectionToDelete.name})}
          onClose={() => setConnectionToDelete(null)}
          onDelete={() => {
            deleteMerchantConnection(connectionToDelete.id);
            setConnectionToDelete(null);
          }}
        />
      )}
      {connectionToConfigure && (
        <EditConnectionModal
          onClose={() => setConnectionToConfigure(null)}
          appId={appSpec.id}
          existingConnection={connectionToConfigure}
        />
      )}
    </Fragment>
  );
};
export default ConnectionsTable;
